import { useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import { hideChromeToolbarPlaybackControls } from "../classes/ChromeToolbarPlaybackControlsSingleton";
import { logger } from "../datadog/logger";
import { toast } from "../toasts";
import type { RoomDelegator } from "../video-sdk";
import {
  dataChannelAppStateAtom,
  DEFAULT_DATA_CHANNEL_APP_STATE,
  roomAtom,
  useLeaveRoom,
  videoSDKAtom,
} from "../video-sdk";
import useScreenShareCallbacks from "./controls/useScreenShareCallbacks";
import { useRevertVideoQualityCallback } from "./controls/useVideoQualityCallbacks";

export type DisconnectTwilioRoomCallback = (options?: {
  shouldEndRoom?: boolean;
  onError?: (error: unknown) => void;
}) => Promise<RoomDelegator | undefined>;

/**
 * Disconnects from the video call room and clears relevant Jotai state.
 */
export function useDisconnectRoomCallback(): DisconnectTwilioRoomCallback {
  const setVideoSDKContext = useSetAtom(videoSDKAtom);
  const getRoom = useAtomCallback(useCallback((get) => get(roomAtom), []));
  const leaveRoom = useLeaveRoom();
  const { stopScreenShare } = useScreenShareCallbacks();
  const revertVideoQuality = useRevertVideoQualityCallback();
  const setDataChannelAppState = useSetAtom(dataChannelAppStateAtom);

  return useCallback(
    async function disconnectFromRoom(options) {
      const room = getRoom();
      if (!room) return;

      try {
        stopScreenShare();
        await leaveRoom({ shouldEndRoom: options?.shouldEndRoom });

        setVideoSDKContext((prev) => ({
          ...prev,
          room: undefined,
          participants: [],
          connectedAt: undefined,
          remoteMediaByParticipantId: {},
          shouldOverrideSuggestedRoomVideoQuality: false,
        }));
        revertVideoQuality();
        setDataChannelAppState(DEFAULT_DATA_CHANNEL_APP_STATE);
        // side effect
        hideChromeToolbarPlaybackControls();
        return room;
      } catch (error) {
        toast.error("There was an issue disconnecting from the room.", {
          purpose: "system",
        });
        logger.error(
          "Unable to disconnect from room",
          {
            roomSid: room.sid,
            localParticipantSid: room.localParticipant?.sid,
          },
          error as Error,
        );
        options?.onError?.(error);
        return;
      }
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );
}
